import {postRequestsendCreditData} from "@/api/user/home";
import {CreditDataMain} from "@/types/api/user/home.type.data";
import {numberSeparator, toPersianNumber} from "@/hooks/useThousandsSeparator";
import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import {Grid} from "@mui/material";
import {GridLegacy} from "@mui/material";
import {useMutation} from "@tanstack/react-query";
import {AxiosError} from "axios";
import React, {useMemo, useState} from "react";
import {toast} from "react-toastify";

type ResultOfInformationReviewProps = {
    children?: React.ReactNode;
    status: string;
    isSuccess?: boolean;
    userInquiry?: CreditDataMain | undefined;
};

interface UserCheck {
    id: number;
    check_type: string;
    price_letter: string;
    check_date_char: string;
    check_date: any;
    price: any;
}

interface SuccessContentProps {
    isSeveralBankDocuments?: boolean;
    userInquiry?: CreditDataMain | undefined;
    setUploadedImagesArray: any;
    uploadedImagesArray: any;
}

const initialChecks = [
    {
        id: 1,
        amount: "750,000,000 ریال",
        date: "1403/06/12",
        defaultText: "چک ضمانت",
        positionedTexts: [
            {text: "750,000,000 ریال", top: "10px", left: "20px"},
            {text: "1403/06/12", top: "40px", left: "20px"},
        ],
        uploadedImage: null,
    },
];

export const SuccessContent = ({
                                   isSeveralBankDocuments,
                                   userInquiry,
                                   setUploadedImagesArray,
                                   uploadedImagesArray
                               }: SuccessContentProps) => {
    const [checks, setChecks] = useState(initialChecks);

    const groupedChecks = useMemo(() => {
        if (!userInquiry?.userCheck) return {};
        return userInquiry.userCheck.reduce((acc, check) => {
            if (!acc[check.check_type]) acc[check.check_type] = [];
            acc[check.check_type].push(check);
            return acc;
        }, {} as Record<string, UserCheck[]>);
    }, [userInquiry?.userCheck]);

    const handleUpload = (event: React.ChangeEvent<HTMLInputElement>, checkId: number) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            setUploadedImagesArray((prev: any) => ({
                ...prev,
                [checkId]: reader.result as string,
            }));
        };
        reader.readAsDataURL(file);
    };

    function formatDateWithSpacing(date: string, spacer: string = " ") {
        const toPersianDigit = (digit: string) => "۰۱۲۳۴۵۶۷۸۹"[parseInt(digit, 10)];
        const digitsOnly = date.replace(/\D/g, "");
        const persianDigits = digitsOnly.split("").map(toPersianDigit);
        return persianDigits.join(spacer);
    }

    function formatNumberWithPlaceholders(number: string | number, totalLength: number = 15, spacer: string = "-", gap: string = " "): string {
        const toPersianDigit = (d: string) => "۰۱۲۳۴۵۶۷۸۹"[parseInt(d, 10)];
        const numberStr = String(number).replace(/\D/g, "");
        const persianDigits = numberStr.split("").map(toPersianDigit);
        const paddingLength = Math.max(totalLength - persianDigits.length, 0);
        const placeholders = Array(paddingLength).fill(spacer);
        const finalArray = [...persianDigits];
        return finalArray.join(" ");
    }

    const toPersianDigits = (str: string) =>
        str.replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[+d]);

    // If no userInquiry or no userCheck, return null or loading state
    if (!userInquiry || !userInquiry.userCheck) {
        return (
            <StackKit sx={{width: "100%", p: 4}} alignItems="center">
                <TypoGraphyKit variant="body1">در حال بارگذاری اطلاعات...</TypoGraphyKit>
            </StackKit>
        );
    }

    return (
        <Grid container gap={2} sx={{display: "flex", minHeight: "600px"}}>
            {Object.entries(groupedChecks).map(([checkType, checks]) => {
                // Dynamic title and instruction based on check type
                const checkTitles: Record<string, string> = {
                    guarantee: "چک ضمانت",
                    installment: "چک اقساط",
                };

                const instructionMessages: Record<string, string[]> = {
                    guarantee: [
                        `کاربر گرامی ؛ اعتبار قابل پرداخت برای شما مبلغ ${toPersianNumber(numberSeparator(userInquiry.price))} میباشد. در صورت تائید مدارک خواسته شده را تکمیل و ارسال نمایید`,
                        "طبق بررسی‌های صورت گرفته؛ شما مجاز به دریافت اعتبار مورد نظر خود می‌باشید.",
                        "لطفاً چک ضمانت را با مشخصات زیر تکمیل کرده و تصویر آن را بارگذاری کنید.",
                    ],
                    installment: [
                        "چک‌های اقساط خود را طبق جزئیات زیر تکمیل کرده و بارگذاری نمایید.",
                    ],
                };

                return (
                    <StackKit sx={{width: "100%"}} key={checkType} gap={2}>
                        <TypoGraphyKit variant="h6" sx={{mb: 2}}>
                            {checkTitles[checkType] || "چک نامشخص"}
                        </TypoGraphyKit>

                        {instructionMessages[checkType]?.map((msg, i) => (
                            <TypoGraphyKit key={i} variant="body1">
                                {msg}
                            </TypoGraphyKit>
                        ))}

                        <Grid container spacing={3}>
                            {(checks as UserCheck[]).map((check, index) => {
                                const formatted = check?.check_date
                                    ?.split("/")
                                    .map((part: string) => part.split("").join(" "))
                                    .join(" ");

                                const formatNumber = (
                                    value: string | number,
                                    length = 15,
                                    gap = "  "
                                ) => {
                                    const str = String(value);
                                    const padded = str.padStart(length, "0");
                                    return padded.split("").join(gap);
                                };

                                // Dynamic positioning data for check fields
                                const overlayFields = [
                                    {
                                        text: toPersianNumber(formatted?.split("").join("\u00A0") || ""),
                                        sx: {
                                            top: "5%",
                                            right: "16%",
                                            fontSize: "10px",
                                        },
                                    },
                                    {
                                        text: check?.check_date_char,
                                        sx: {
                                            top: "14%",
                                            right: "19%",
                                            fontSize: "10px",
                                        },
                                    },
                                    {
                                        text: check?.price_letter,
                                        sx: {
                                            top: "33%",
                                            right: "29%",
                                            fontSize: "12px",
                                        },
                                    },
                                    {
                                        text: toPersianNumber("شرکت تجارت الکترونیک پاریز همکار"),
                                        sx: {
                                            top: "46%",
                                            right: "9%",
                                            fontSize: "12px",
                                        },
                                    },
                                    {
                                        text: toPersianNumber("14010154664"),
                                        sx: {
                                            top: "46%",
                                            left: "28%",
                                            fontSize: "12px",
                                        },
                                    },
                                    {
                                        text: toPersianNumber(formatNumber(check?.price || "", 15, "  ")),
                                        sx: {
                                            top: "68%",
                                            left: "7%",
                                            fontSize: "11px",
                                        },
                                    },
                                ];

                                return (
                                    <StackKit key={check.id} alignItems="center" justifyContent={'center'} width={'100%'}>
                                        <GridLegacy item xs={12} sm={6} md={4} sx={{
                                            position: "relative",
                                            maxHeight: "350px",
                                            overflow: "auto",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            paddingBottom: 2
                                        }}>
                                            <img
                                                src={uploadedImagesArray[check.id] || '/check.jpg'}
                                                alt="check"
                                                style={{
                                                    width: "100%",
                                                    height: "auto",
                                                    borderRadius: "8px",
                                                    objectFit: "contain",
                                                    display: "block",
                                                    margin: "0 auto"
                                                }}
                                            />
                                            {/* Only show overlay fields when NO image is uploaded (using default image) */}
                                            {!uploadedImagesArray[check.id] &&
                                                overlayFields.map((field, idx) => (
                                                    <TypoGraphyKit
                                                        key={idx}
                                                        sx={{
                                                            position: "absolute",
                                                            color: "blue",
                                                            direction: "ltr",
                                                            fontWeight: "600",
                                                            ...field.sx
                                                        }}
                                                    >
                                                        {field.text}
                                                    </TypoGraphyKit>
                                                ))}
                                        </GridLegacy>

                                        <CustomButton component="label" sx={{marginTop: "50px", width: {xs: "100%", sm: "auto"}}}>
                                            بارگذاری تصویر
                                            <input type="file" hidden onChange={(e) => handleUpload(e, check.id)}/>
                                        </CustomButton>
                                    </StackKit>
                                );
                            })}
                        </Grid>

                        {/* Dynamic footnotes section */}
                        <StackKit gap={1}>
                            <TypoGraphyKit variant="body1">
                                لطفاً چک را در وجه شرکت تجارت الکترونیک پاریز همکار با شناسه ملی{" "}
                                {toPersianNumber("14010154664")} بابت{" "}
                                {checkType === "guarantee" ? "ضمانت" : "اقساط"} در سامانه ثبت کنید.
                            </TypoGraphyKit>

                            <TypoGraphyKit variant="body1">
                                مبلغ چک{" "}
                                {checkType === "guarantee" ? "ضمانت" : "اقساط"} :{" "}
                                {toPersianNumber(numberSeparator(userInquiry?.userCheck?.[0]?.price))} ریال
                            </TypoGraphyKit>

                            <TypoGraphyKit variant="body1">
                                تاریخ چک{" "}
                                {checkType === "guarantee" ? "ضمانت" : "اقساط"} :{" "}
                                {toPersianNumber(`${userInquiry?.userCheck?.[0]?.check_date}`)}
                            </TypoGraphyKit>
                        </StackKit>
                    </StackKit>
                );
            })}
        </Grid>
    );
};

export const ErrorContent = () => {
    return (
        <>
            <Grid>
                <TypoGraphyKit variant="body1">
                    طبق بررسی های صورت گرفته ؛ شما مجاز به دریافت اعتبار نمیباشید.
                </TypoGraphyKit>
                <TypoGraphyKit variant="body1" sx={{color: "red"}}>
                    علت عدم تخصیص اعتبار: تصویر چک ارسالی با مشخصات ضامن مغایرت دارد.
                </TypoGraphyKit>
            </Grid>
        </>
    );
};

/**
 * ResultOfInformationReview
 *
 * کامپوننت نمایش نتیجه بررسی اطلاعات اعتبار کاربر.
 * این کامپوننت شامل دو بخش اصلی است:
 * - SuccessContent: نمایش اطلاعات موفق شامل چک‌های ضمانت و اقساط با قابلیت بارگذاری تصویر.
 * - ErrorContent: نمایش پیام خطا در صورت عدم تایید اعتبار.
 *
 * ویژگی‌ها:
 * - نمایش وضعیت نتیجه اعتبارسنجی (موفق / ناموفق).
 * - گروه‌بندی چک‌ها بر اساس نوع (ضمانت یا اقساط).
 * - نمایش توضیحات و راهنمایی‌های مرتبط با چک‌ها.
 * - بارگذاری تصاویر چک‌ها با قابلیت ذخیره‌سازی در state محلی و ارسال به سرور.
 * - دکمه ارسال تصاویر اصلاح شده به سرور با استفاده از API.
 *
 * @param {Object} props
 * @param {string} props.status وضعیت نتیجه بررسی ("موفق" یا "ناموفق")
 * @param {boolean} [props.isSuccess] تعیین موفقیت بررسی اطلاعات
 * @param {CreditDataMain} [props.userInquiry] داده‌های اعتبار کاربر
 * @param {React.ReactNode} [props.children] محتوای دلخواه
 *
 * @component
 * @returns JSX.Element
 */

const ResultOfInformationReview = (props: ResultOfInformationReviewProps) => {
    const {children, status, isSuccess, userInquiry} = props;

    const {
        mutate: fetchRequestCreateUserBankAccount,
        isPending: isPendingCreateUserBankAccount,
    } = useMutation({
        mutationFn: postRequestsendCreditData,
        onSuccess: (data: any) => {
            window.location.reload();
        },
        onError: (error: AxiosError<{ message: string }>) => {
            toast.error(error?.response?.data?.message);
        },
    });

    const onSubmit = () => {
        if (!userInquiry?.userCheck) return;
        const guaranteeChecks = userInquiry.userCheck
            .filter((check) => check.check_type === "guarantee")
            .map((check, index) => ({
                image: uploadedImagesArray[check.id] || null,
                id: check?.id,
            }));

        const installmentChecks = userInquiry.userCheck
            .filter((check) => check.check_type === "installment")
            .map((check, index) => ({
                image: uploadedImagesArray[check.id] || null,
                id: check?.id,
            }));
        const requestBody = {
            installment: installmentChecks,
            guarantee: guaranteeChecks,
        };
        fetchRequestCreateUserBankAccount(requestBody);
    };

    const [uploadedImagesArray, setUploadedImagesArray] = useState<Record<number, string>>(() =>
        userInquiry?.userCheck?.reduce((acc, check) => {
            acc[check.id] = "";
            return acc;
        }, {} as Record<number, string>) || {}
    );

    return (
        <Grid container sx={{width: "100%", height: "100%", padding: {xs: 2, sm: 3, md: 4}}}>
            <StackKit alignItems={"center"} justifyContent={"space-between"}
                      sx={{height: "100%", width: "100%", gap: {xs: 2, sm: 3}}}>
                {/* Header section */}
                <Grid sx={{display: "flex", flexDirection: {xs: "column", sm: "row"}, justifyContent: "center", alignItems: "center", textAlign: "center", gap: {xs: 1, sm: 2}, width: "100%"}}>
                    <TypoGraphyKit variant="h6">نتیجه بررسی اطلاعات :</TypoGraphyKit>
                    <TypoGraphyKit sx={{color: "green"}} variant="h6">
                        {status}
                    </TypoGraphyKit>
                </Grid>

                {/* Main content section */}
                <Grid sx={{width: "100%", display: "flex", justifyContent: "center", alignItems: "center"}}>
                    {userInquiry?.status_manager_answer === "accept" || userInquiry?.status_manager_answer === "await_accept_from_user" ? (
                        <SuccessContent isSeveralBankDocuments userInquiry={userInquiry} setUploadedImagesArray={setUploadedImagesArray} uploadedImagesArray={uploadedImagesArray}
                        />
                    ) : (
                        <ErrorContent/>
                    )}
                </Grid>

                {/* Button section */}
                <StackKit direction={{xs: "column", sm: "row"}} sx={{width: "100%", justifyContent: {xs: "center", sm: "end"}, alignItems: "center", marginTop: {xs: 2, sm: 3}, gap: {xs: 1.5, sm: 0}}}>
                    <CustomButton onClick={onSubmit} disabled={Object.values(uploadedImagesArray).some((img) => img === "")} sx={{width: {xs: "100%", sm: "auto"}, height: "48px", fontSize: {xs: "14px", sm: "16px"}}}>
                        ارسال عکس
                    </CustomButton>
                </StackKit>
            </StackKit>
        </Grid>
    );
};

export default ResultOfInformationReview;